import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ErrorStateService } from '../../services/common/error-state.service';
import { AuthService } from '../../services/auth/auth.service';

@Component({
  selector: 'app-error-page',
  standalone: true,
  imports: [CommonModule, ButtonModule, CardModule],
  template: `
    <div class="bg-[#F4F7FC] flex flex-col min-h-screen">
      <div class="flex flex-grow justify-center items-center">
        <p-card styleClass="!border !border-[#dde1e6] !shadow-none !p-0">
          <div class="w-[350px] xlplus:w-[400px] h-auto p-6">
            <div class="flex justify-center mb-6">
              @if (errorType() === 'network') {
                <i class="pi pi-wifi text-[4rem] text-[#ef4444]"></i>
              } @else if (errorType() === 'service-unavailable') {
                <i class="pi pi-server text-[4rem] text-[#f59e0b]"></i>
              } @else {
                <i class="pi pi-exclamation-triangle text-[4rem] text-[#ef4444]"></i>
              }
            </div>

            <h1 class="font-lato font-bold text-xl xlplus:text-2xl text-center text-[#1e293b] mb-3">{{ errorTitle() }}</h1>
            <p class="font-lato font-normal text-sm xlplus:text-base text-center text-[#64748b] mb-6 leading-relaxed">{{ errorMessage() }}</p>

            <div class="flex flex-col gap-3">
              <p-button
                label="Try Again"
                icon="pi pi-refresh"
                (onClick)="retry()"
                [loading]="isRetrying()"
                styleClass="!w-full !bg-taxpod-blue !border-taxpod-blue !font-lato !font-bold !text-sm xlplus:!text-base disabled:!text-[#83858a] disabled:!bg-[#d5d7da] disabled:!border-[#d5d7da]"
              />
              <!-- <p-button
                label="Go to Login"
                icon="pi pi-sign-in"
                variant="outlined"
                (onClick)="goToLogin()"
                styleClass="!w-full !border-taxpod-blue !text-taxpod-blue !border-[2px] !font-lato !font-bold !text-sm xlplus:!text-base hover:!bg-[#E0F2FE]"
              /> -->
            </div>

            @if (lastAttempt()) {
              <p class="mt-4 font-lato text-xs xlplus:text-sm text-center text-[#94a3b8]">Last attempt: {{ lastAttempt() | date:'medium' }}</p>
            }
          </div>
        </p-card>
      </div>
      <footer class="h-[62px] p-6 flex justify-center items-center text-[12px] xlplus:text-[12px] text-gray-400 font-lato">
        Copyright © {{ currentYear }} YYC Holdings Sdn. Bhd.
      </footer>
    </div>
  `,
  styles: []
})
export class ErrorPageComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private errorStateService = inject(ErrorStateService);
  private auth = inject(AuthService);

  currentYear: number = new Date().getFullYear();
  errorType = signal<string>('unknown');
  errorTitle = signal<string>('Something went wrong');
  errorMessage = signal<string>('An unexpected error occurred. Please try again.');
  isRetrying = signal<boolean>(false);
  lastAttempt = signal<Date | null>(null);

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const type = params['type'] || 'unknown';
      this.errorType.set(type);
      this.updateErrorContent(type);
    });

    // Also check error state service
    const currentError = this.errorStateService.currentError();
    if (currentError) {
      this.lastAttempt.set(currentError.timestamp);
    }
  }

  private updateErrorContent(type: string): void {
    switch (type) {
      case 'network':
        this.errorTitle.set('500 Internal Server Error');
        this.errorMessage.set(
          'The server is currently unavailable. Please try again later or contact support if the problem persists.'
        );
        break;
      case 'service-unavailable':
        this.errorTitle.set('503 Service Unavailable');
        this.errorMessage.set(
          'The service is temporarily unavailable. Our team is working on it. Please try again later.'
        );
        break;
      case 'server-error':
        this.errorTitle.set('500 Internal Server Error');
        this.errorMessage.set(
          'An unexpected error occurred on the server. Please try again later or contact support if the problem persists.'
        );
        break;
      default:
        this.errorTitle.set('500 Internal Server Error');
        this.errorMessage.set('An unexpected error occurred. Please try again.');
    }
  }

  retry(): void {
    this.isRetrying.set(true);
    this.errorStateService.clearError();

    // Small delay before retry to show loading state
    setTimeout(() => {
      this.isRetrying.set(false);
      // Try to navigate back to dashboard - the guard will check auth again
      this.router.navigateByUrl(this.auth.homeUrl());
    }, 1000);
  }

  goToLogin(): void {
    this.errorStateService.clearError();
    this.router.navigate(['/auth/login']);
  }
}
